import { z } from "zod";
/**
 * CIK is the join key everywhere in Mosaic, never the ticker — tickers are reused,
 * retired and reassigned, and a spin-off is precisely the event that mints a new one.
 * Always the 10-digit zero-padded form, because that is what data.sec.gov's URLs want.
 */
export const Cik = z.string().regex(/^\d{10}$/, "CIK must be 10 digits, zero-padded");
/**
 * A CIK in its 10-digit zero-padded form, from any spelling of it.
 *
 * Does not validate: non-digits are dropped, and more than 10 digits come back unpadded
 * and unchanged in length.
 *
 * @param raw a CIK as a number or string, padded or not
 * @returns the digits, left-padded with zeros to 10
 */
export function toCik(raw) {
    return String(raw).replace(/\D/g, "").padStart(10, "0");
}
/**
 * A CIK in its 10-digit zero-padded form, or null when `raw` is not one.
 *
 * @param raw a CIK as written: one to ten digits, surrounding whitespace allowed
 * @returns the zero-padded CIK; null for anything else
 */
export function parseCik(raw) {
    const trimmed = raw.trim();
    return /^\d{1,10}$/.test(trimmed) ? toCik(trimmed) : null;
}
/** Accession number in its canonical dashed form: 0001234567-24-000123. */
export const Accession = z
    .string()
    .regex(/^\d{10}-\d{2}-\d{6}$/, "Accession must look like 0001234567-24-000123");
/**
 * An accession number in its dashed form, from a dashed or undashed spelling.
 *
 * @param raw an accession number, with or without dashes
 * @returns the dashed form; `raw` unchanged when it does not hold exactly 18 digits
 */
export function toAccession(raw) {
    const digits = raw.replace(/\D/g, "");
    if (digits.length !== 18)
        return raw;
    return `${digits.slice(0, 10)}-${digits.slice(10, 12)}-${digits.slice(12)}`;
}
/** Accession with dashes stripped — the form EDGAR's Archives paths use. */
export function accessionNoDashes(accession) {
    return accession.replace(/-/g, "");
}
/**
 * Calendar date, no time, no zone. EDGAR reports in US/Eastern; the date is stored as
 * filed and never reformatted for a locale.
 */
export const IsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
    /*
     * "2025-02-30" matches the regex and would raise in Postgres as `::date`; rejecting it
     * here lets a misread date fail validation instead of throwing deep inside a job.
     */
    .refine(isRealCalendarDate, "Not a real calendar date");
/** Round-trips through UTC: a date the calendar does not have normalises to another. */
export function isRealCalendarDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return false;
    const [, y, m, d] = match;
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return false;
    const asUtc = new Date(Date.UTC(year, month - 1, day));
    return (asUtc.getUTCFullYear() === year &&
        asUtc.getUTCMonth() === month - 1 &&
        asUtc.getUTCDate() === day);
}
/** An RFC 3339 date-time, with an explicit offset or `Z`. */
export const Timestamp = z.string().datetime({ offset: true });
/** A plain decimal string, the form money takes on the wire and in Postgres `numeric`. */
export const DecimalString = z
    .string()
    .regex(/^-?\d+(\.\d+)?$/, "Must be a plain decimal string");
/**
 * An amount of money. `amount` is a decimal string in major units, never a float;
 * arithmetic on it happens in Postgres `numeric` or a decimal library, never in
 * JavaScript numbers.
 */
export const Money = z.object({
    amount: DecimalString,
    currency: z.string().length(3).default("USD"),
});
/** A distribution ratio: `numerator` SpinCo shares for every `denominator` parent shares. */
export const Ratio = z.object({
    numerator: z.number().int().positive(),
    denominator: z.number().int().positive(),
});
/**
 * A distribution ratio for display, as `numerator : denominator`.
 *
 * @param r the ratio to render
 * @returns the display text
 */
export function formatRatio(r) {
    return `${r.numerator} : ${r.denominator}`;
}
/**
 * The key that marks a value as a range the FILING stated, not one Mosaic computed.
 *
 * One constant for `isStatedRange` and the SQL jsonb key test, so the two cannot drift.
 */
export const RANGE_KEY = "range";
/**
 * A term a filing states as a span rather than a number.
 *
 * A filing may state a "contemplated distribution ratio of 1.0 to 3.0 shares". The span
 * is stored as stated; its midpoint or either end would be a figure the filing does not
 * contain.
 *
 * Wrapped in `RANGE_KEY` rather than a bare `{min, max}` so it cannot be mistaken for an
 * object-valued fact such as `spinco_debt`.
 */
export function StatedRange(inner) {
    return z.object({ [RANGE_KEY]: z.object({ min: inner, max: inner }) });
}
/** Whether a stored fact value is a stated range. Shape-only; never throws on a scalar. */
export function isStatedRange(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return false;
    const r = value[RANGE_KEY];
    return typeof r === "object" && r !== null && "min" in r && "max" in r;
}
/**
 * Whether a fact may be written onto the situation's own columns and counted by the stage.
 *
 * A tentative value (the filing calls it assumed) and a stated range (the filing declines
 * to give one) are both held back: neither is a known term. The one predicate every
 * reader of "settled" uses.
 */
export function isSettledValue(f) {
    return !f.tentative && !isStatedRange(f.value);
}
/**
 * A stated range for display, as `min – max`.
 *
 * @param inner formats one end of the range
 * @param value a fact value, which may or may not be a stated range
 * @returns the display text, or `null` when `value` is not a stated range
 */
export function formatRange(inner, value) {
    if (!isStatedRange(value))
        return null;
    return `${inner(value[RANGE_KEY].min)} – ${inner(value[RANGE_KEY].max)}`;
}
/** How a derived value came to exist. `user` always wins and is never auto-replaced. */
export const Method = z.enum(["rule", "llm", "user"]);
/**
 * A location inside a stored document. Offsets index the *extracted text*, not the
 * source HTML, and are computed by locating the quote rather than trusted from a model.
 */
export const SourceRef = z.object({
    documentId: z.string().uuid(),
    accession: Accession,
    charStart: z.number().int().nonnegative(),
    charEnd: z.number().int().nonnegative(),
    quote: z.string().min(1),
});
/** An opaque pagination cursor for the next page; `null` when there is none. */
export const Cursor = z.string().nullable();
/**
 * The schema of one page of a list endpoint: `{ items, total, cursor }`.
 *
 * @param item the schema of one item
 * @returns the page schema
 */
export function paged(item) {
    return z.object({ items: z.array(item), total: z.number().int().nonnegative(), cursor: Cursor });
}
//# sourceMappingURL=primitives.js.map